import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { flexRender, useTable } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import type { CandidateListItemDto } from '@sara/api-client';
import { cn } from '@sara/ui/lib/utils';
import { DataPanel } from '@sara/ui/components/data-panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sara/ui/components/table';
import { candidateColumns, features } from './columns';

interface CandidateListTableProps {
  items: CandidateListItemDto[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onRowClick: (candidate: CandidateListItemDto) => void;
  isFetching: boolean;
  searchTerm?: string;
  footer?: ReactNode;
}

export function CandidateListTable({
  items,
  sorting,
  onSortingChange,
  onRowClick,
  isFetching,
  searchTerm,
  footer,
}: CandidateListTableProps) {
  // oxlint-disable-next-line react/incompatible-library -- `table` is only read inline below (never passed to a memoized child/hook), so React Compiler's stale-closure concern for TanStack Table doesn't apply here.
  const table = useTable({
    features,
    data: items,
    columns: candidateColumns,
    state: { sorting },
    onSortingChange,
    manualSorting: true,
  });

  if (items.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        {searchTerm ? (
          <>
            <p className="font-display text-xl font-medium text-foreground">
              No candidates match &ldquo;{searchTerm}&rdquo;.
            </p>
            <p className="mt-2 max-w-xs text-sm text-muted-foreground">
              Try a different search term.
            </p>
          </>
        ) : (
          <>
            <p className="font-display text-xl font-medium text-foreground">No candidates yet.</p>
            <p className="mt-2 max-w-xs text-sm text-muted-foreground">
              Invite a candidate to get started.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <DataPanel
      className={cn('flex min-h-0 flex-1 flex-col', isFetching && 'opacity-60 transition-opacity')}
    >
      <DataPanel.Body className="min-h-0 flex-1 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-secondary/95 backdrop-blur-sm [&_th]:border-b [&_th]:border-border">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => onRowClick(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataPanel.Body>
      {footer && <DataPanel.Footer>{footer}</DataPanel.Footer>}
    </DataPanel>
  );
}
